/** The words and shapes of a catalog record's Usage tab (plan §10.4, §9.5): the used-by's
 *  groups — one `type_key` each — with each group's total from the first page's
 *  `available_types`, in the record's own order. Pure. */
import type { UsedByTypeOption } from '@/api/usedBy';
import { notCounted, type SummaryUnavailableReason } from '@/features/catalog/words';
import { formatZoned } from '@/utils/zonedTime';

/** One relation of a Usage tab, as the record declares it. */
export interface UsageGroupDef<K extends string> {
  key: K;
  /** "Tours". */
  label: string;
  /** What its records are, in a sentence: "tours" ("Loading the tours…", "No tours match"). */
  noun: string;
  /** What a count-only group (counted, never listed) says in place of rows. */
  unlisted?: string;
}

/** One relation with its total, before any search. */
export interface UsageGroupTotal<K extends string> {
  key: K;
  count: number;
  /** The backend stopped counting at `count`: read "1,000+". */
  capped: boolean;
  /** Counted, never listed: no rows, no search. */
  countOnly: boolean;
}

/** The record's relations that hold anything, in the record's order, with their totals from
 *  `available_types` (a type the record does not declare is left out). */
export function usageTotals<K extends string>(
  groups: readonly UsageGroupDef<K>[],
  types: readonly UsedByTypeOption[],
): UsageGroupTotal<K>[] {
  return groups.flatMap((g) => {
    const t = types.find((x) => x.key === g.key);
    const count = t?.count ?? 0;
    return count > 0
      ? [{ key: g.key, count, capped: !!t?.capped, countOnly: !!t?.count_only }]
      : [];
  });
}

/** A total in words: "9", "1,000+". */
export function usageCountText(t: Pick<UsageGroupTotal<string>, 'count' | 'capped'>): string {
  return `${t.count.toLocaleString('en-US')}${t.capped ? '+' : ''}`;
}

/** A group's count while a search narrows it: "3 of 9"; the total alone without a search. */
export function usageMatchText(total: Pick<UsageGroupTotal<string>, 'count' | 'capped'>, matches: number | null): string {
  const all = usageCountText(total);
  return matches === null ? all : `${matches.toLocaleString('en-US')} of ${all}`;
}

/** What a used-by summary (D7, lazy) carries for the Usage tab's line — the activity's and the
 *  itinerary's share these fields. */
export interface UsageSummaryFacts {
  available: boolean;
  reason: SummaryUnavailableReason;
  tours: number | null;
  capped: boolean;
  upcoming_departures: number | null;
  upcoming_bookings: number | null;
  next_departure: string | null;
}

const n = (v: number) => v.toLocaleString('en-US');
const plural = (v: number, one: string, many = `${one}s`) => `${n(v)} ${v === 1 ? one : many}`;

/** The Usage tab's summary line: the tours (the record's words for them — `tours(count,
 *  capped)`), their upcoming departures with the next one's day, and their upcoming bookings
 *  — "9 tours · 14 upcoming departures, next 14 Oct · 212 upcoming bookings". A read the
 *  database cancelled says so ("Tours not counted (timed out)"); no tours, the record's
 *  `none`; above the cap (500 tours) no departure was counted, and the line says that. */
export function usageSummaryText(
  s: UsageSummaryFacts,
  {
    tours,
    none,
    zone,
    now = new Date(),
  }: { tours: (count: number, capped: boolean) => string; none: string; zone?: string | null; now?: Date },
): string {
  if (!s.available || s.tours === null) return notCounted('Tours', s.reason);
  if (s.tours === 0) return none;
  const counted = tours(s.tours, s.capped);
  if (s.capped || s.upcoming_departures === null) return `${counted} · departures not counted above ${n(s.tours)} tours`;
  if (s.upcoming_departures === 0) return `${counted} · no upcoming departures`;
  const next = s.next_departure
    ? `, next ${formatZoned(s.next_departure, zone || 'UTC', { weekday: false, time: false, zone: false, now })}`
    : '';
  const bookings = s.upcoming_bookings === null ? null : plural(s.upcoming_bookings, 'upcoming booking');
  return [counted, `${plural(s.upcoming_departures, 'upcoming departure')}${next}`, bookings].filter(Boolean).join(' · ');
}
